import React, { Fragment, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import injectSheet from 'react-jss';
import Progress from 'react-progress';
import PropTypes from 'prop-types';
import Nav from '../Nav';
import Spinner from '../../components/Spinner';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import ProfileMenu from '../../components/ProfileMenu';
import Hamburger from '../../components/Hamburger';
import GlobalMessage from '../../components/GlobalMessage';
import { appRoutes } from '../../root/routes';
import {
  checkIfMobile,
  initApp,
  resetApiStatus,
  setApiError
} from '../../root/actions';
import { dispatch } from '../../root/store';
import config from '../../config/app';
import style from './style';
import Phone from '../Phone';
import useAuth from '../../root/useAuth';
import LogoSVG from '../../assets/icons/ThorneHealthLogo.svg';
import { checkInterationStatus, getLoginClick, setLoginClick } from '../../config/msal-auth';

const getReduxData = state => {
  return {
    isAppInit: state.containers.app.get('isInit'),
    user: state.containers.app.get('user'),
    appHasError: state.api.get('hasError'),
    appErrorMsg: state.api.get('error'),
    isLoading: state.api.get('isLoading') || false,
    highlightedNavbarSection: state.containers.nav.get(
      'highlightedNavbarSection'
    ),
    isMenuToggled: state.containers.nav.get('isMenuToggled'),
    progressBarPercent: state.containers.app.get('progressBarPercent'),
    isMobile: state.containers.app.get('isMobile'),
    router: state.router
  };
};

const App = ({ classes }) => {
  const {
    isAppInit,
    user,
    appHasError,
    appErrorMsg,
    highlightedNavbarSection,
    isMenuToggled,
    progressBarPercent,
    isMobile, isLoading
  } = useSelector(getReduxData);
  const navigate = useNavigate();
  const { b2cLogin } = useAuth(false);

  const isUserRegistered = user && user.active;

  const goToSignup = () => {
    navigate('/signup');
  };

  const gotoPrivacyPolicy = () => {
    console.log('Clicked privacy')
    navigate('/privacypolicy')
  }

  const gotoContact = () => {
    console.log('Clicked contactUs')
    navigate('/contactus')
  }

  const loginLoaderHandler = () => {
    if (checkInterationStatus() < 3 && getLoginClick() == 'true'){
      setLoginClick(false) 
      dispatch(resetApiStatus())
    }
  }

  var loginTimer = undefined
  useEffect(() =>{
    if (getLoginClick() == 'true')
      loginTimer = setTimeout(loginLoaderHandler, 2000);
    return (()=>{
      clearTimeout(loginTimer)
    })
   
  },[])


  const renderAuthView = () => {
    return (
      <div>
     {!isLoading ?  
        (
        <>
         <div className={classes.auth}>
          <div className={classes.authBrandContainer}>
            <img
              src={LogoSVG}
              className="auth-logo"
              onClick={() => navigate('/')}
              alt="header"
            />
          </div>
          <div className={classes.authMainContainer}>
            <Phone onSigninClick={b2cLogin} onSignupClick={goToSignup} />
          </div>
        </div>
        <p style={{
          width: '100%',
          fontSize: '12px',
          textAlign: 'right',
          paddingRight: '56px',
          position: 'fixed',
          bottom: '0',
        }}>
          <span style={{ cursor: 'pointer' }} onClick={gotoPrivacyPolicy}>Privacy Policy</span> | <span style={{ cursor: 'pointer' }}
            onClick={gotoContact}>Contact Us</span>
        </p>
      </>
      ): (<><Spinner /></>)}
    </div>);
  };

  const renderAppView = () => {
    const {
      profileMenu,
      hamburgerMenu,
      footer,
      desktopHeader,
      mobileHeader
    } = config;

    profileMenu.fullName = user.full_name;

    let sidebarClassName = 'sidebar';
    let mainContentClassName = 'main-content';

    if (!isMenuToggled) {
      sidebarClassName = `${sidebarClassName} hideOnMobile`;
    } else {
      mainContentClassName = `${mainContentClassName} hideOnMobile`;
    }

    return (
      <div className="app">
        <div className={sidebarClassName}>
          {!isMobile && <Header {...desktopHeader} />}
          <Nav {...config.nav} />
          <Footer {...footer} isMenuToggled={isMenuToggled} />
        </div>
        <div className="main">
          <div className="main-header">
            <div className="main-flex">
              {isMobile && <Header {...mobileHeader} />}
              <ProfileMenu {...profileMenu} />
              <Hamburger {...hamburgerMenu} isActive={isMenuToggled} />
            </div>
            {!isMenuToggled && renderHeaderDivider()}
          </div>
          <div className={mainContentClassName}>{appRoutes}</div>
        </div>
      </div>
    );
  };

  // Renders the Divider thats between the body and the header.
  const renderHeaderDivider = () => {
    const progressBarStyle = {
      display: 'block',
      position: 'relative',
      top: '-2px',
      left: 'none',
      boxShadow: 'none'
    };

    // Render gray header divider if updating profile on mobile.
    return (
      <Fragment>
        {isMobile ? (
          <div className="mobile-divider-container">
            {highlightedNavbarSection === 'updateProfile' ? (
              <hr className="gradient-divider" />
            ) : (
              <Fragment>
                <hr className="light-gradient-divider" />
                <Progress
                  percent={progressBarPercent}
                  className="progress-bar-main"
                  color="#D3B2FC"
                  height={2}
                  style={progressBarStyle}
                />
              </Fragment>
            )}
          </div>
        ) : (
          <hr className="gradient-divider desktop" />
        )}
      </Fragment>
    );
  };

  // Let user know when their connection has disconnected.
  const updateOnlineStatus = () => {
    if (!window.navigator.onLine) {
      dispatch(
        setApiError('You have been disconnected from the internet.', {
          isIgnoreTimeout: true
        })
      );
    } else {
      dispatch(
        setApiError('Your internet connection has been restored.', {
          isSuccess: true
        })
      );
    }
  };

  return (
    <div className="full-container">
      {!isAppInit ? <Spinner /> : <Fragment>{renderAuthView()}</Fragment>}
    </div>
  );
};

App.propTypes = {
  user: PropTypes.object,
  appHasError: PropTypes.bool,
  appErrorMsg: PropTypes.string,
  resetApiStatus: PropTypes.func,
  isAppInit: PropTypes.bool,
  classes: PropTypes.object,
  changeRoute: PropTypes.func,
  isMenuToggled: PropTypes.bool,
  isMobile: PropTypes.bool,
  highlightedNavbarSection: PropTypes.string,
  progressBarPercent: PropTypes.number
};

export default injectSheet(style)(App);
